import React, { useContext, useMemo, useState } from 'react';
import {
  Accordions,
  AccordionsItem,
  AccordionsItemHeader,
  AccordionsItemSection,
  Badge,
  Button,
  Checkbox,
  CondensedHeader,
  CondensedLarge,
  FluidButtonLayout,
  Form,
  FormGroup,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  SectionHeader,
  TaxonomyUtil,
  ToastUtil,
  Well,
  useBreakpoint,
  CandidateTaxonomyUtil,
  DateUtil,
} from '@axiom/ui';
import type {
  Candidate,
  Experience,
  Taxonomy,
  TaxonomyFocusAreaEnum,
  TaxonomyLegalSkillEnum,
} from '@axiom/validation';

import { ExperienceApi } from '../../api/experience-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { WindowUtil } from '../../utils/window-util';
import { CandidateProfileHighlightsApi } from '../../api/candidate-profile-highlights-api';
import { CandidateProfileTopFocusAreasApi } from '../../api/candidate-profile-top-focus-areas-api';

import type { ExperienceSectionStateType } from './ExperienceEditContext';
import { ExperienceEditContext } from './ExperienceEditContext';
import {
  ExperienceEditSectionKeys,
  ExperienceEditUtil,
} from './experience-edit-util';
import { ExperienceEditFormSkillsReviewModal } from './ExperienceEditFormSkillsReviewModal';
import { ExperienceEditSkillsSchema } from './experience-edit-skills-schema';
import { ExperienceEditFormSkillsDeleteFocusAreaModal } from './ExperienceEditFormSkillsDeleteFocusAreaModal';

const { SKILLS } = ExperienceEditSectionKeys;

export type ExperienceEditFormSkillsBodyType = {
  candidate: Candidate;
  experience: Experience;
  taxonomy: Taxonomy;
  name?: string;
};

export const ExperienceEditFormSkillsBody = ({
  candidate,
  experience,
  taxonomy,
  name = 'EXPERIENCE_EDIT_FORM_SKILLS_BODY',
}: ExperienceEditFormSkillsBodyType) => {
  const {
    dirtyFormState,
    setDirtyFormState,
    accordionState,
    setAccordionState,
  } = useContext(ExperienceEditContext);
  const { experienceTaxonomy = [], experiencesFocusAreaOrderPreferences = [] } =
    experience;

  const { isMobile } = useBreakpoint();

  const [isReviewSkillsModalOpen, setIsReviewSkillsModalOpen] = useState(false);
  const [
    isConfirmDeleteFocusAreaModalOpen,
    setIsConfirmDeleteFocusAreaModalOpen,
  ] = useState(false);
  const [shouldMarkNoFocusAreasApply, setShouldMarkNoFocusAreasApply] =
    useState(false);
  const [deletedFocusAreas, setDeletedFocusAreas] = useState<string[]>([]);

  const focusAreaOrder: TaxonomyFocusAreaEnum[] =
    experiencesFocusAreaOrderPreferences?.[0]?.focusAreas ?? [];

  const generalSkillOptions = TaxonomyUtil.getGeneralSkillOptions(
    taxonomy.generalSkills
  );

  const isCandidateAlp = !TaxonomyUtil.isLawyer(candidate.businessTitle);

  const fieldConfiguration = useMemo(() => {
    return ExperienceEditUtil.getSkillsSectionConfiguration(
      focusAreaOrder,
      taxonomy,
      isCandidateAlp
    );
  }, [focusAreaOrder, taxonomy, isCandidateAlp]);

  const initialLegalSkillsByFocusArea = useMemo(
    () => ExperienceEditUtil.getLegalSkillsByFocusArea(experienceTaxonomy),
    [experienceTaxonomy]
  );

  const dynamicSchema = ExperienceEditSkillsSchema(focusAreaOrder, taxonomy);

  const [
    openFocusAreaSectionsWithDsSuggestions,
    setOpenFocusAreaSectionsWithDsSuggestions,
  ] = useState(
    experienceTaxonomy.reduce<Record<TaxonomyFocusAreaEnum, boolean>>(
      (acc, curr) => ({
        ...acc,
        ...(curr.dsSuggestion ? { [curr.focusArea]: false } : {}),
      }),
      {}
    )
  );

  const updateSectionHasBeenOpened = (focusArea: TaxonomyFocusAreaEnum) => {
    setOpenFocusAreaSectionsWithDsSuggestions(prev => ({
      ...prev,
      [focusArea]: true,
    }));
  };

  const shouldModalOpen = () =>
    Object.values(openFocusAreaSectionsWithDsSuggestions).includes(false);

  return (
    <Form
      name="EXPERIENCE_EDIT_FORM_SKILLS"
      schema={dynamicSchema}
      initialValues={{
        generalSkills: experience.generalSkills ?? [],
        ...initialLegalSkillsByFocusArea,
      }}
      onSubmit={async formData => {
        if (shouldModalOpen() && !isReviewSkillsModalOpen) {
          setIsReviewSkillsModalOpen(true);
          return;
        }

        const totalLegalSkills = focusAreaOrder.reduce<
          Record<TaxonomyFocusAreaEnum, TaxonomyLegalSkillEnum[]>
        >((acc, curr) => {
          if (!deletedFocusAreas.includes(curr) && curr) {
            const focusArea =
              formData[curr] ?? initialLegalSkillsByFocusArea[curr];
            if (focusArea) acc[curr] = focusArea;
          }
          return acc;
        }, {});

        const legalSkills = Object.keys(totalLegalSkills).flatMap(
          key => totalLegalSkills[key] ?? []
        );

        await ExperienceApi.updateExperience(experience.id, {
          generalSkills:
            formData.generalSkills || (experience.generalSkills ?? []),
          ...(shouldMarkNoFocusAreasApply
            ? {
                noFocusAreasApplyMarkedAt: DateUtil.formatAsTimestamp(
                  new Date()
                ),
              }
            : {}),
        });
        await ExperienceApi.updateCandidateExperienceTaxonomy(
          candidate.id,
          experience.id,
          [
            ...CandidateTaxonomyUtil.orderExperienceTaxonomyByFocusAreaOrderPreferences(
              // @ts-expect-error Needs type fix for APCORE-2687
              experienceTaxonomy
                .filter(
                  et =>
                    !et.legalSkills && !deletedFocusAreas.includes(et.focusArea)
                )
                .map(({ id, ...rest }) => ({ ...rest })),
              experiencesFocusAreaOrderPreferences
            ),
            ...CandidateTaxonomyUtil.convertLegalSkillsToExperienceTaxonomy(
              legalSkills,
              experience.id,
              taxonomy
            ),
          ]
        );
        await CandidateExperienceApi.refreshExperiences(candidate.id);
        await CandidateExperienceApi.refreshGroupedExperiences(candidate.id);
        await CandidateProfileIssuesApi.refreshCandidateProfileIssues(
          candidate.id
        );
        await CandidateProfileHighlightsApi.refreshProfileHighlights(
          candidate.id
        );
        await CandidateProfileTopFocusAreasApi.refreshProfileTopFocusAreas(
          candidate.id
        );

        setAccordionState({
          ...ExperienceEditUtil.setAllToFalse(accordionState),
        });
        WindowUtil.scrollToTop();

        ToastUtil.add({
          name: 'SUCCESS_SKILLS',
          type: 'info',
          children: <>Your skills for {experience.client} have been saved!</>,
        });

        setIsReviewSkillsModalOpen(false);
      }}
    >
      {({ dirty, fireSubmit, values }) => {
        // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
        setTimeout(() => {
          if (dirtyFormState[SKILLS] !== dirty) {
            setDirtyFormState((prevState: ExperienceSectionStateType) => ({
              ...prevState,
              [SKILLS]: dirty,
            }));
          }
        }, 0);

        return (
          <>
            <div data-test={name}>
              <Well background="blue">
                <SectionHeader name="EXPERIENCE_EDIT_FORM_SKILLS_WELL_HEADER">
                  What skills did you use while at{' '}
                  {ExperienceEditUtil.getExperienceClientDateRangeLabel(
                    experience
                  )}
                  ?
                </SectionHeader>
                <Gutter bottom="8px" />
                <Paragraph>
                  Select the specific skills that you used during this
                  experience. We’ve selected some skills for you based on the
                  work description you provided. You can remove, add or edit
                  skills to your liking.
                </Paragraph>
              </Well>
              <Gutter bottom="16px" />
              <Accordions name="FOCUS_AREA_GROUP_ACCORDION">
                {fieldConfiguration
                  .filter(
                    ({ focusArea }) =>
                      !deletedFocusAreas.includes(focusArea.key)
                  )
                  .map(
                    (
                      { focusArea, skillGroups },
                      index,
                      filteredFieldConfiguration
                    ) => {
                      const focusKey: TaxonomyFocusAreaEnum = focusArea.key;
                      const selectedCount = values[focusKey]?.length ?? 0;
                      const hasSectionNotBeenOpened =
                        !openFocusAreaSectionsWithDsSuggestions[focusKey];

                      return (
                        <AccordionsItem
                          key={focusArea.key}
                          name={`FOCUS_AREA_GROUP_${index}`}
                        >
                          <AccordionsItemHeader
                            onToggle={isExpanded => {
                              if (isExpanded && hasSectionNotBeenOpened) {
                                updateSectionHasBeenOpened(focusArea.key);
                              }
                            }}
                          >
                            <Layout position="space-between middle">
                              <LayoutItem>
                                <CondensedHeader name="FOCUS_AREA_TITLE">
                                  {focusArea.fullName}
                                </CondensedHeader>
                              </LayoutItem>
                              <LayoutItem position="center">
                                <Gutter right="16px">
                                  <Badge
                                    name="LEGAL_SKILLS_COUNT"
                                    background={
                                      selectedCount > 0 ? 'gray' : 'red'
                                    }
                                  >
                                    {selectedCount}
                                  </Badge>
                                </Gutter>
                              </LayoutItem>
                            </Layout>
                          </AccordionsItemHeader>
                          <AccordionsItemSection
                            name={`FOCUS_AREA_GROUP_${index}_SECTION`}
                          >
                            <Gutter bottom="24px" />
                            <FormGroup name={focusArea.key}>
                              <Layout
                                direction="vertical"
                                verticalGutter="16px"
                              >
                                {skillGroups.map(
                                  ({ legalSkills, skillGroup }) => (
                                    <Well
                                      key={skillGroup.key}
                                      name="SKILL_GROUP_LEGAL_SKILLS"
                                    >
                                      <CondensedHeader name="talentQuestion">
                                        {skillGroup.talentQuestion}
                                      </CondensedHeader>
                                      <Gutter bottom="8px" />
                                      <Layout
                                        direction="horizontal"
                                        horizontalGutter="8px"
                                        verticalGutter="8px"
                                        stackableOn="mobile"
                                        wrap
                                      >
                                        {legalSkills.map(legalSkill => (
                                          <Checkbox
                                            key={legalSkill.key}
                                            name={focusArea.key}
                                            displayValue={legalSkill.fullName}
                                            option={legalSkill.key}
                                            mode={
                                              isMobile ? 'checkbox' : 'pill'
                                            }
                                          />
                                        ))}
                                      </Layout>
                                    </Well>
                                  )
                                )}
                              </Layout>
                            </FormGroup>
                            {(filteredFieldConfiguration.length > 1 ||
                              !experience.isAxiom) && (
                              <Gutter className="text-align-right" top="16px">
                                <a
                                  data-test={`DELETE_FOCUS_AREA_${focusArea.key}`}
                                  href="#"
                                  onClick={e => {
                                    e.preventDefault();
                                    if (
                                      filteredFieldConfiguration.length === 1
                                    ) {
                                      setIsConfirmDeleteFocusAreaModalOpen(
                                        true
                                      );
                                      return;
                                    }
                                    setDeletedFocusAreas([
                                      ...deletedFocusAreas,
                                      focusArea.key,
                                    ]);
                                  }}
                                >
                                  Delete {focusArea.fullName}
                                </a>
                              </Gutter>
                            )}
                            {filteredFieldConfiguration.length === 1 &&
                            isConfirmDeleteFocusAreaModalOpen ? (
                              <ExperienceEditFormSkillsDeleteFocusAreaModal
                                name="EXPERIENCE_EDIT_FORM_SKILLS_DELETE_FOCUS_AREA_MODAL"
                                onClose={() => {
                                  setIsConfirmDeleteFocusAreaModalOpen(false);
                                }}
                                onSubmit={() => {
                                  setDeletedFocusAreas([
                                    ...deletedFocusAreas,
                                    focusArea.key,
                                  ]);
                                  setIsConfirmDeleteFocusAreaModalOpen(false);
                                  setShouldMarkNoFocusAreasApply(true);
                                }}
                              />
                            ) : null}
                          </AccordionsItemSection>
                        </AccordionsItem>
                      );
                    }
                  )}
                <AccordionsItem name="GENERAL_SKILLS">
                  <AccordionsItemHeader>
                    <Layout position="space-between middle">
                      <LayoutItem>
                        <CondensedHeader>
                          Optionally, do any of these other skills apply to this
                          experience?
                        </CondensedHeader>
                      </LayoutItem>
                      <LayoutItem position="center">
                        <Gutter right="16px">
                          <Badge background="gray" name="GENERAL_SKILLS_COUNT">
                            {values.generalSkills?.length}
                          </Badge>
                        </Gutter>
                      </LayoutItem>
                    </Layout>
                  </AccordionsItemHeader>
                  <AccordionsItemSection>
                    <Gutter bottom="24px" />
                    <Well>
                      <FormGroup name="generalSkills">
                        <CondensedHeader>
                          General Skills (optional)
                        </CondensedHeader>
                        <Gutter bottom="8px" />
                        <Layout
                          horizontalGutter="8px"
                          verticalGutter="8px"
                          stackableOn="mobile"
                          wrap
                        >
                          {generalSkillOptions.map(({ label, value }) => (
                            <Checkbox
                              key={value}
                              name="generalSkills"
                              displayValue={label}
                              option={value}
                              mode={isMobile ? 'checkbox' : 'pill'}
                            />
                          ))}
                        </Layout>
                      </FormGroup>
                    </Well>
                  </AccordionsItemSection>
                </AccordionsItem>
              </Accordions>
              <Gutter bottom="16px" />
              <CondensedLarge>
                Do you have suggestions for additional skills to add? Send
                feedback to{' '}
                <a href="mailto:skills-feedback@axiomlaw.com?subject=Skills suggestions&body=Dear Axiom Product Team,%0D%0A%0D%0AI'd like to recommend that Axiom add the following skill(s):%0D%0A%0D%0ASkill:%0D%0AFocus Area that this skill should be added to:">
                  skills-feedback@axiomlaw.com
                </a>
              </CondensedLarge>
              <Gutter bottom="16px" />
              <FluidButtonLayout>
                <Button
                  pattern="secondary"
                  onClick={async () => {
                    await fireSubmit();
                  }}
                  name="EXPERIENCE_EDIT_FORM_SKILLS_SAVE"
                >
                  Save
                </Button>
              </FluidButtonLayout>
            </div>
            {isReviewSkillsModalOpen ? (
              <ExperienceEditFormSkillsReviewModal
                onClose={() => {
                  setIsReviewSkillsModalOpen(false);
                }}
                onSubmit={async () => {
                  await fireSubmit();
                }}
              />
            ) : null}
          </>
        );
      }}
    </Form>
  );
};
